(() => {
  class DeferredAppBlock extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.template = this.querySelector(':scope > template[data-deferred-app-template]');
      if (!this.template) return;

      if (this.dataset.designMode === 'true' || !('IntersectionObserver' in window)) {
        this.hydrate();
        return;
      }

      const margin = Number.parseInt(this.dataset.observerMargin, 10) || 1000;
      this.observer = new IntersectionObserver(
        (records) => {
          if (!records.some((record) => record.isIntersecting)) return;
          this.observer.disconnect();
          this.hydrate();
        },
        { rootMargin: `${margin}px 0px` }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async hydrate() {
      if (!this.template?.isConnected) return;
      const content = this.template.content.cloneNode(true);
      content.querySelectorAll('script').forEach((inertScript) => {
        const executableScript = document.createElement('script');
        [...inertScript.attributes].forEach(({ name, value }) => executableScript.setAttribute(name, value));
        executableScript.textContent = inertScript.textContent;
        inertScript.replaceWith(executableScript);
      });
      this.template.replaceWith(content);
      this.dataset.hydrated = 'true';

      try {
        await this.loadVendorScript();
        await this.waitUntilReady();
        this.dataset.ready = 'true';
      } catch (error) {
        this.dataset.loadError = 'true';
        console.warn('[Culture House] Deferred app block failed to load; keeping its fallback link.', error);
      }
    }

    loadVendorScript() {
      const source = this.dataset.vendorScriptUrl;
      if (!source) return Promise.resolve();

      const url = new URL(source, window.location.href);
      if (
        this.dataset.deferredLoader === 'instafeed' &&
        (url.hostname !== 'cdn.nfcube.com' || !/^\/instafeed-[a-f0-9]+\.js$/.test(url.pathname))
      ) {
        document.dispatchEvent(new CustomEvent('culture-house:vendor-url-drift', { detail: { source } }));
        return Promise.reject(new Error(`Unexpected Instafeed vendor URL: ${source}`));
      }

      window.CultureHouseVendorScripts ||= new Map();
      if (window.CultureHouseVendorScripts.has(url.href)) {
        return window.CultureHouseVendorScripts.get(url.href);
      }

      const existing = [...document.scripts].find((script) => script.src === url.href);
      if (
        existing &&
        (existing.dataset.loaded === 'true' || existing.readyState === 'complete' || window.instafeedApp)
      ) {
        return Promise.resolve();
      }

      const promise = new Promise((resolve, reject) => {
        const script = existing || document.createElement('script');
        script.addEventListener('load', () => {
          script.dataset.loaded = 'true';
          resolve();
        }, { once: true });
        script.addEventListener('error', reject, { once: true });
        if (!existing) {
          script.src = url.href;
          script.async = true;
          script.dataset.deferredVendor = this.dataset.deferredLoader || 'app';
          document.head.append(script);
        }
      });
      window.CultureHouseVendorScripts.set(url.href, promise);
      return promise;
    }

    waitUntilReady() {
      const feed = this.querySelector('#insta-feed');
      if (!feed || feed.childElementCount > 0) return Promise.resolve();

      return new Promise((resolve, reject) => {
        const timeout = window.setTimeout(() => {
          observer.disconnect();
          reject(new Error('Deferred app did not render within 10 seconds.'));
        }, 10000);
        const observer = new MutationObserver(() => {
          if (feed.childElementCount === 0) return;
          window.clearTimeout(timeout);
          observer.disconnect();
          resolve();
        });
        observer.observe(feed, { childList: true, subtree: true });
      });
    }
  }

  if (!customElements.get('deferred-app-block')) {
    customElements.define('deferred-app-block', DeferredAppBlock);
  }

  class CultureHouseDeferredMarquee extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.template = this.querySelector(':scope > template[data-marquee-clone]');
      if (!this.template || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      if (this.dataset.designMode === 'true' || !('IntersectionObserver' in window)) {
        this.hydrate();
        return;
      }

      this.observer = new IntersectionObserver(
        (records) => {
          if (!records.some((record) => record.isIntersecting)) return;
          this.observer.disconnect();
          this.scheduleHydration();
        },
        { rootMargin: '1000px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    scheduleHydration() {
      const whenIdle = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(() => this.hydrate(), { timeout: 1500 });
        else window.setTimeout(() => this.hydrate(), 100);
      };
      if (document.readyState === 'complete') whenIdle();
      else window.addEventListener('load', whenIdle, { once: true });
    }

    hydrate() {
      if (!this.template?.isConnected) return;
      this.template.replaceWith(this.template.content.cloneNode(true));
      this.dataset.hydrated = 'true';
    }
  }

  if (!customElements.get('culture-house-deferred-marquee')) {
    customElements.define('culture-house-deferred-marquee', CultureHouseDeferredMarquee);
  }

  const configElement = document.getElementById('ThemeDeferredModules');
  if (!configElement) return;

  let entries = [];
  try {
    entries = JSON.parse(configElement.textContent);
  } catch (error) {
    console.error('Could not parse deferred theme modules.', error);
    return;
  }

  const loaded = new Map();
  const scheduled = new Set();
  const elementEntries = new WeakMap();
  const observed = new WeakSet();
  const rootMargin = '300px 0px';

  function load(entry) {
    if (!entry?.url) return Promise.resolve();
    if (!loaded.has(entry.url)) {
      loaded.set(
        entry.url,
        import(entry.url).catch((error) => {
          loaded.delete(entry.url);
          console.error(`Could not load ${entry.url}.`, error);
        })
      );
    }
    return loaded.get(entry.url);
  }

  function requestLoad(entry) {
    if (!entry.afterLoad) return load(entry);
    if (loaded.has(entry.url) || scheduled.has(entry.url)) return loaded.get(entry.url) || Promise.resolve();
    scheduled.add(entry.url);
    const whenIdle = () => {
      const run = () => {
        scheduled.delete(entry.url);
        load(entry);
      };
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1500 });
      else window.setTimeout(run, 100);
    };
    if (document.readyState === 'complete') whenIdle();
    else window.addEventListener('load', whenIdle, { once: true });
    return Promise.resolve();
  }

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        (records) => {
          records.forEach((record) => {
            if (!record.isIntersecting) return;
            const matchingEntries = elementEntries.get(record.target) || [];
            matchingEntries.forEach(requestLoad);
            observer.unobserve(record.target);
          });
        },
        { rootMargin }
      )
    : null;

  function registerElement(element, entry) {
    if (!element) return;
    const matchingEntries = elementEntries.get(element) || [];
    if (!matchingEntries.includes(entry)) matchingEntries.push(entry);
    elementEntries.set(element, matchingEntries);

    if (!observer) {
      requestLoad(entry);
    } else if (!observed.has(element)) {
      observed.add(element);
      observer.observe(element);
    }
  }

  function scan(root = document) {
    entries.forEach((entry) => {
      if (!entry.selector || entry.interactionOnly) return;
      if (root instanceof Element && root.matches(entry.selector)) registerElement(root, entry);
      root.querySelectorAll?.(entry.selector).forEach((element) => registerElement(element, entry));
    });
  }

  function loadForInteraction(event) {
    if (!(event.target instanceof Element)) return;
    entries.forEach((entry) => {
      if (entry.selector && event.target.closest(entry.selector)) requestLoad(entry);
    });
  }

  ['pointerover', 'pointerdown', 'focusin', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, loadForInteraction, { capture: true, passive: eventName !== 'focusin' });
  });

  scan();

  const mutationObserver = new MutationObserver((records) => {
    records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    }));
  });
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
